/**
 * Explicit logged-out marker — cookie-mode logout suppression.
 *
 * The platform chain authenticates by an HttpOnly refresh cookie alone, and its
 * bootstrap (`refreshOnLoad`) must always attempt a silent refresh because both
 * client-visible signals are structurally dark (login returns `refreshToken:
 * null`; the sentinel cookie is scoped under `/api/v1/_platform` and invisible
 * to `document.cookie`). That leaves logout with a hole: `POST
 * /_platform/auth/logout` is best-effort and currently 500s on cookie-only
 * calls, so the server-side cookie can survive — and the next boot would
 * silently log the user straight back in.
 *
 * This marker records "the user explicitly logged out here": set by
 * `performLogout`, cleared by `commitVerifiedSession` on the next successful
 * login. While present, bootstrap skips the silent refresh. It is a UX
 * correctness seam only — actual revocation is the server's job.
 *
 * AUDIENCE-SCOPED: admin (HR Admin) and mobile (Employee Web) deliberately
 * share an origin in prod, and platform shares localhost in dev. A global
 * marker would let logging out of one app block — or a login to one app
 * un-block — the OTHER app's session restore. Both the storage key and the
 * cookie name carry the audience configured by this app's bootstrap.
 *
 * Two persistent transports + memory:
 *   1. localStorage — normal case; survives reloads, shared across tabs.
 *   2. A non-HttpOnly root-path cookie — storage-disabled fallback. The
 *      restore-risk this marker guards against requires a WORKING refresh
 *      cookie, so "cookies disabled" implies "nothing to restore"; the cookie
 *      transport therefore covers every case localStorage misses.
 *   3. Module memory — same-realm belt for environments where both throw.
 */
import { getAudience } from "../refresh/authNetworkConfig.js";

const STORAGE_KEY_PREFIX = "linkhr:auth:loggedOut:";
const COOKIE_PREFIX = "linkhr_logged_out_";

function storageKey(aud?: string): string {
  return STORAGE_KEY_PREFIX + (aud ?? getAudience());
}

function cookieName(aud?: string): string {
  return COOKIE_PREFIX + (aud ?? getAudience());
}

let memoryFallback = false;

function tryLocalStorage<T>(op: (ls: Storage) => T, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const ls = window.localStorage;
    if (!ls) return fallback;
    return op(ls);
  } catch {
    return fallback;
  }
}

function cookieIsSet(): boolean {
  if (typeof document === "undefined") return false;
  return new RegExp(`(?:^|;\\s*)${cookieName()}=1(?:;|$)`).test(document.cookie);
}

function writeCookie(maxAge: number, aud?: string): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${cookieName(aud)}=1; path=/; max-age=${maxAge}; SameSite=Strict`;
  } catch {
    // Cookie write blocked — localStorage/memory transports still apply.
  }
}

export const loggedOutMarker = {
  set(): void {
    memoryFallback = true;
    tryLocalStorage((ls) => ls.setItem(storageKey(), "1"), undefined);
    writeCookie(31_536_000); // 1 year; cleared by the next successful login
  },
  isSet(): boolean {
    return (
      tryLocalStorage((ls) => ls.getItem(storageKey()) === "1", false) ||
      cookieIsSet() ||
      memoryFallback
    );
  },
  clear(): void {
    memoryFallback = false;
    tryLocalStorage((ls) => ls.removeItem(storageKey()), undefined);
    writeCookie(0);
  },

  /** Clear ANOTHER audience's marker — the audience exchange establishes a
   * live session for the target app, so its earlier explicit-logout latch must
   * not veto the bootstrap. Memory fallback untouched (it is this app's). */
  clearForAudience(aud: string): void {
    tryLocalStorage((ls) => ls.removeItem(storageKey(aud)), undefined);
    writeCookie(0, aud);
  },
};
